import { breakpoints } from 'components/theme';
import type { ModalFunc } from 'antd/lib/modal/confirm';
import type { CSSProperties } from 'react';
import React from 'react';
import { merge } from 'lodash';

export enum ModalType {
  CONFIRM = 'confirm', // two buttons modal: confirm modal.
  INFO = 'info', // one button modal: info/success/error/warning
}

const chatController = (() => {
  const getChatRoot = () =>
    document.getElementById('brokerchat-inactive') ??
    document.getElementById('brokerchat-root');

  let prevChatStyle: string;

  const hideChat = () => {
    if (!getChatRoot()) return;
    const currentChatStyle = getChatRoot().style.display;
    if (currentChatStyle === 'none') return;
    prevChatStyle = currentChatStyle;
    getChatRoot().style.display = 'none';
  };

  const showChat = () => {
    if (!getChatRoot() || prevChatStyle === undefined) return;
    getChatRoot().style.display = prevChatStyle;
    prevChatStyle = undefined;
  };

  return {
    hide: hideChat,
    show: showChat,
  };
})();

const isPromise = (value: any) => typeof value === 'object' && typeof value.then === 'function';

export const makeModalResponsive = (
  createdModal: ReturnType<ModalFunc>,
  options?: {
    modalType?: ModalType;
    dataCys?: { ok?: string; cancel?: string; content?: string };
  },
) => {
  const { modalType, dataCys } = options || {};
  // same logic as useBreakpoint for usage outside of react components
  const getBreakpoint = () => {
    const results = {
      xs: true,
      sm: true,
      md: true,
      lg: true,
      xl: true,
      xxl: true,
    };
    Object.keys(breakpoints).forEach((key: keyof typeof breakpoints) => {
      const point = breakpoints[key];
      results[key] = window.innerWidth >= point;
    });
    return results;
  };

  const getResponsiveStyle = () => {
    const { md } = getBreakpoint();

    const buttonsProps =
      !md && modalType
        ? {
            [ModalType.CONFIRM]: {
              okButtonProps: {
                style: { width: 'calc(100vw / 2 - 36px)' },
              },
              cancelButtonProps: {
                style: { width: 'calc(100vw / 2 - 36px)' },
              },
            },
            [ModalType.INFO]: {
              okButtonProps: {
                style: { width: 'calc(100vw - 64px)' },
              },
            },
          }[modalType]
        : {};

    merge(buttonsProps, {
      okButtonProps: { 'data-cy': dataCys?.ok ?? 'ok-button' },
      cancelButtonProps: { 'data-cy': dataCys?.cancel ?? 'cancel-button' },
    });

    return {
      transitionName: !md ? 'ant-move-down' : undefined,
      width: !md ? '100vw' : undefined,
      style: !md
        ? ({
            position: 'fixed',
            left: 0,
            bottom: 0,
            top: 'unset',
            maxWidth: 'unset',
            margin: 'unset',
            padding: 'unset',
          } as CSSProperties)
        : undefined,
      ...buttonsProps,
    };
  };

  const onResize = () => {
    const { md } = getBreakpoint();
    if (md) chatController.show();
    else chatController.hide();

    createdModal.update(prevConfig => ({
      ...prevConfig,
      ...getResponsiveStyle(),
    }));
  };
  onResize();

  window.addEventListener('resize', onResize);
  const cleanup = () => {
    chatController.show();
    window.removeEventListener('resize', onResize);
  };
  createdModal.update(prevConfig => ({
    ...prevConfig,
    zIndex: 5000,
    modalRender: node => {
      const nodeToRender = prevConfig.modalRender
        ? prevConfig.modalRender(node)
        : node;
      if (!dataCys?.content) return nodeToRender;
      return React.createElement(
        'div',
        { 'data-cy': dataCys.content },
        nodeToRender,
      );
    },
    afterClose: cleanup,
    onOk: (...args) => {
      if (prevConfig.onOk) {
        const result = prevConfig.onOk(...args);
        if (isPromise(result)) {
          return result.then(cleanup);
        }
      }
      cleanup();
    },
    onCancel: (...args) => {
      if (prevConfig.onCancel) {
        const result = prevConfig.onCancel(...args);
        if (isPromise(result)) {
          return result.then(cleanup);
        }
      }
      cleanup();
    },
  }));
};
